import React, { useState, useRef, useEffect } from 'react';
import { Package, AlertTriangle, Loader2 } from 'lucide-react';

interface GateBag {
  id: number;
  bag_code: string;
}

/**
 * Ventana bloqueante que obliga a escanear/confirmar una bolsa al arrancar el
 * batch, antes de poder cargar productos. Cubre toda la pantalla y no se puede
 * cerrar — la única salida es un scan de bolsa exitoso.
 *
 * Razón: con el modelo de bolsa global (ADR-46) una bolsa abierta se hereda
 * entre batches, así que un batch nuevo ya no pedía bolsa. Esta compuerta
 * restaura ese pedido sin tocar el modelo global: re-escanear la misma bolsa
 * abierta sólo la confirma (no-op en el backend).
 */
export function BagGateModal({
  activeBag,
  onConfirmed,
}: {
  activeBag: GateBag | null;
  onConfirmed: (bagCode: string) => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // 2026-09-10 — dos correos: elección de correo cuando ya hay una bolsa abierta por correo.
  const [choice, setChoice] = useState<{
    code: string;
    options: Array<{ key: string; name: string; color: string }>;
  } | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function chooseCourier(courierKey: string) {
    if (!choice || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/warehouse/bags/scan', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bag_code: choice.code, courier_key: courierKey }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Error al abrir la bolsa' }));
        setError(err.error ?? 'Error al abrir la bolsa');
        setLoading(false);
        return;
      }
      setChoice(null);
      onConfirmed(choice.code);
    } catch {
      setError('Error de conexión al abrir la bolsa');
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/warehouse/bags/scan', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bag_code: trimmed }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Error al abrir la bolsa' }));
        // 2026-09-10 — dos correos: ya hay una bolsa por correo → elegir cuál se reemplaza.
        if (err?.code === 'NEED_COURIER_CHOICE' && Array.isArray(err?.meta?.options)) {
          setChoice({ code: trimmed, options: err.meta.options });
          setLoading(false);
          return;
        }
        setError(err.error ?? 'Error al abrir la bolsa');
        setLoading(false);
        return;
      }
      onConfirmed(trimmed);
    } catch {
      setError('Error de conexión al abrir la bolsa');
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-2xl bg-[var(--color-bg-card)] p-6 shadow-2xl ring-4 ring-yellow-500">
        <div className="mb-3 flex items-center gap-3">
          <Package size={28} className="text-yellow-600" />
          <h2 className="text-lg font-bold text-[var(--color-text)]">
            Escaneá la bolsa para empezar
          </h2>
        </div>

        {activeBag ? (
          <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
            Hay una bolsa abierta:{' '}
            <span className="font-mono font-bold text-[var(--color-text)]">
              {activeBag.bag_code}
            </span>
            . Escaneala de nuevo para confirmar que vas a usar esa, o escaneá otra para cambiar.
          </p>
        ) : (
          <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
            No hay ninguna bolsa abierta. Escaneá una bolsa antes de empezar a cargar productos.
          </p>
        )}

        {choice && (
          <div className="mb-4 space-y-2 rounded-xl border border-[var(--color-border)] p-3">
            <p className="text-sm font-semibold text-[var(--color-text)]">
              Ya hay una bolsa abierta por cada correo. ¿Para qué correo es la bolsa{' '}
              <span className="font-mono">{choice.code}</span>?
            </p>
            <div className="grid gap-2">
              {choice.options.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  disabled={loading}
                  onClick={() => chooseCourier(c.key)}
                  className="rounded-xl px-4 py-3 text-base font-bold text-white hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: c.color }}
                >
                  Es para {c.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setChoice(null)}
              className="text-xs text-[var(--color-text-secondary)] underline"
            >
              Cancelar y escanear otra bolsa
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Escaneá el código de la bolsa..."
            className="w-full rounded-xl border-2 border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3 text-lg font-mono text-[var(--color-text)] outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30"
          />
          {error && (
            <p className="flex items-center gap-1.5 text-sm text-red-500">
              <AlertTriangle size={14} className="shrink-0" /> {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-yellow-600 px-4 py-3 text-base font-bold text-white hover:bg-yellow-700 disabled:opacity-50"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Package size={18} />}
            Confirmar bolsa y empezar
          </button>
        </form>
      </div>
    </div>
  );
}
